import { useEffect, useState } from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { BsChevronRight } from "react-icons/bs";
import { Module } from "../../types";
import {
  practiceQuestionTypeLabel,
  PracticeQuestionType,
  sumQuestionTypeRemaining,
  sumQuestionTypeTotals,
} from "../../utils/practiceQuestionTypes";
import "./QuickPracticeTypeModal.scss";

type QuickPracticeTypeModalProps = {
  show: boolean;
  onHide: () => void;
  /** Types with total > 0 in the current module selection. */
  availableTypes: PracticeQuestionType[];
  modulesInSelection: Module[];
  onConfirm: (types: PracticeQuestionType[] | undefined) => void;
};

function TypeOptionContent({
  label,
  remaining,
  total,
}: {
  label: string;
  remaining: number;
  total: number;
}) {
  const allCompleted = total > 0 && remaining === 0;

  return (
    <>
      <span className="quick-practice-type-modal__option-label">{label}</span>
      <span className="quick-practice-type-modal__option-trailing">
        {allCompleted ? (
          <span className="quick-practice-type-modal__option-meta">
            <span className="quick-practice-type-modal__option-status">
              All {total} questions completed
            </span>
            <span className="quick-practice-type-modal__pill">continue anyway</span>
          </span>
        ) : (
          <span className="quick-practice-type-modal__option-count">
            {remaining} remaining of {total}
          </span>
        )}
        <BsChevronRight
          className="quick-practice-type-modal__option-arrow"
          aria-hidden
        />
      </span>
    </>
  );
}

const QuickPracticeTypeModal = ({
  show,
  onHide,
  availableTypes,
  modulesInSelection,
  onConfirm,
}: QuickPracticeTypeModalProps) => {
  const remainingByType = sumQuestionTypeRemaining(modulesInSelection);
  const totalByType = sumQuestionTypeTotals(modulesInSelection);
  /** `null` means All remaining. */
  const [selectedType, setSelectedType] = useState<PracticeQuestionType | null>(
    null
  );
  const [shakeStart, setShakeStart] = useState(false);

  useEffect(() => {
    if (show) {
      setSelectedType(null);
      setShakeStart(false);
    }
  }, [show]);

  const triggerStartShake = () => {
    setShakeStart(false);
    requestAnimationFrame(() => setShakeStart(true));
    window.setTimeout(() => setShakeStart(false), 550);
  };

  const isAllSelected = selectedType === null;

  const selectAll = () => {
    if (!isAllSelected) {
      triggerStartShake();
    }
    setSelectedType(null);
  };

  const clearSelection = () => setSelectedType(null);

  const selectType = (type: PracticeQuestionType) => {
    if (selectedType === type) return;
    setSelectedType(type);
    triggerStartShake();
  };

  const allRemaining = availableTypes.reduce(
    (sum, type) => sum + remainingByType[type],
    0
  );
  const allTotal = availableTypes.reduce(
    (sum, type) => sum + totalByType[type],
    0
  );

  const selectedTotal = isAllSelected
    ? allTotal
    : totalByType[selectedType];
  const selectedRemaining = isAllSelected
    ? allRemaining
    : remainingByType[selectedType];
  // Prefer remaining; when all done, replay uses the full selected total
  const practiceQuestionCount =
    selectedRemaining > 0 ? selectedRemaining : selectedTotal;

  // Allow starting even with 0 remaining, as long as the selection has questions
  const canStart = selectedTotal > 0;

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      className="quick-practice-type-modal"
    >
      <Modal.Header closeButton closeLabel="Close">
        <Modal.Title>Choose question types</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="quick-practice-type-modal__intro">
          Choose all remaining questions, or practise one type only. You can
          keep practising even if you&apos;ve already completed them.
        </p>

        <div
          className="quick-practice-type-modal__options"
          role="radiogroup"
          aria-label="Question types for quick practice"
        >
          <button
            type="button"
            role="radio"
            className={
              isAllSelected
                ? "quick-practice-type-modal__option quick-practice-type-modal__option--active"
                : "quick-practice-type-modal__option"
            }
            onClick={selectAll}
            aria-checked={isAllSelected}
          >
            <TypeOptionContent
              label="All remaining"
              remaining={allRemaining}
              total={allTotal}
            />
          </button>

          {availableTypes.map((type) => {
            const isActive = selectedType === type;
            return (
              <button
                key={type}
                type="button"
                role="radio"
                className={[
                  "quick-practice-type-modal__option",
                  isActive ? "quick-practice-type-modal__option--active" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                aria-checked={isActive}
                onClick={() => selectType(type)}
              >
                <TypeOptionContent
                  label={`${practiceQuestionTypeLabel(type)} only`}
                  remaining={remainingByType[type]}
                  total={totalByType[type]}
                />
              </button>
            );
          })}
        </div>
      </Modal.Body>
      <Modal.Footer className="quick-practice-type-modal__footer">
        <span className="quick-practice-type-modal__selection-count">
          {canStart
            ? `${practiceQuestionCount} ${
                practiceQuestionCount === 1 ? "question" : "questions"
              }`
            : "No questions selected"}
        </span>
        <div className="quick-practice-type-modal__footer-actions">
          <Button
            variant="danger"
            className="quick-practice-type-modal__clear"
            onClick={clearSelection}
            disabled={isAllSelected}
          >
            Clear
          </Button>
          <Button
            variant="dark"
            className={
              shakeStart ? "quick-practice-type-modal__start--shake" : undefined
            }
            disabled={!canStart}
            onClick={() =>
              onConfirm(isAllSelected ? undefined : [selectedType])
            }
          >
            Start practice
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default QuickPracticeTypeModal;
